import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase } from './lib/supabase'

export type UserRole = 'admin' | 'buyer' | 'supplier' | 'driver'

export interface AuthUser {
  id: string
  email: string
  name: string
  role: UserRole
  isApproved: boolean
  avatarUrl?: string
}

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  isAuthenticated: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  authError: string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)

  const buildUserFromSession = useCallback(async (supabaseUser: User): Promise<AuthUser | null> => {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('role, full_name, is_approved')
      .eq('id', supabaseUser.id)
      .single()

    if (error || !profile || !profile.is_approved) return null

    return {
      id: supabaseUser.id,
      email: supabaseUser.email ?? '',
      name: profile.full_name || supabaseUser.user_metadata?.full_name || '',
      role: profile.role as UserRole,
      isApproved: profile.is_approved,
      avatarUrl: supabaseUser.user_metadata?.avatar_url,
    }
  }, [])

  useEffect(() => {
    let mounted = true

    const initAuth = async () => {
      const { data: { session: supabaseSession } } = await supabase.auth.getSession()
      if (!mounted) return

      if (supabaseSession?.user) {
        const appUser = await buildUserFromSession(supabaseSession.user)
        if (!mounted) return
        if (appUser) setUser(appUser)
      }
      setLoading(false)
    }

    initAuth()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (!mounted) return
      if (event === 'SIGNED_OUT') {
        setUser(null)
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [buildUserFromSession])

  const signInWithGoogle = useCallback(async () => {
    setAuthError(null)
    setLoading(true)
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}` },
      })
      if (error) throw new Error(error.message)
    } catch (error: any) {
      setLoading(false)
      throw error
    }
  }, [])

  const signOut = useCallback(async () => {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-')) localStorage.removeItem(key)
    })
    await supabase.auth.signOut({ scope: 'local' })
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, signInWithGoogle, signOut, authError }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}